import { useState } from "react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageContext";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function Contact() {
  const { T } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", whatsapp: "", message: "" });
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [sending, setSending] = useState(false);

  const submitContact = (e) => {
    e.preventDefault();
    toast.success(T.contact.success);
    setForm({ name: "", email: "", whatsapp: "", message: "" });
  };

  const subscribeNewsletter = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/newsletter/subscribe", { email: newsletterEmail });
      toast.success(T.contact.newsletter_success);
      setNewsletterEmail("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const inputClass = "w-full bg-transparent border-0 border-b border-[#090708]/20 focus:border-[#090708] focus:ring-0 rounded-none px-0 py-3 text-base placeholder:text-[#090708]/30";

  return (
    <div className="pt-16 pb-10 px-6 lg:px-16 max-w-[1200px] mx-auto" data-testid="contact-page">
      <div className="mb-16 max-w-lg">
        <p className="text-[11px] tracking-[0.25em] uppercase text-[#9B6179] mb-3">{T.contact.eyebrow}</p>
        <h1 className="font-serif text-4xl lg:text-5xl text-[#9B6179] mb-4">{T.contact.title}</h1>
        <p className="text-base text-[#090708]/55">{T.contact.subtitle}</p>
      </div>

      <div className="grid lg:grid-cols-12 gap-16">
        <form onSubmit={submitContact} className="lg:col-span-7 space-y-7" data-testid="contact-form">
          <input required placeholder={T.contact.name} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputClass} data-testid="contact-input-name" />
          <input required type="email" placeholder={T.contact.email} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputClass} data-testid="contact-input-email" />
          <input placeholder={T.contact.whatsapp} value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} className={inputClass} data-testid="contact-input-whatsapp" />
          <textarea required rows={3} placeholder={T.contact.message} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={inputClass} data-testid="contact-input-message" />
          <button type="submit" className="bg-[#090708] text-[#F7F0E8] px-8 py-4 text-[11px] tracking-[0.2em] uppercase hover:bg-[#6F3F55] transition-colors duration-300 border border-[#E8C47A]/30" data-testid="contact-submit-button">{T.contact.send}</button>
        </form>

        <div className="lg:col-span-5 space-y-10">
          <div>
            <p className="text-[11px] tracking-widest uppercase text-[#090708]/40 mb-2">{T.contact.address_title}</p>
            <p className="font-serif text-xl text-[#090708]">Bagneux, França</p>
          </div>
          <div>
            <p className="text-[11px] tracking-widest uppercase text-[#090708]/40 mb-2">WhatsApp</p>
            <p className="font-serif text-xl text-[#090708]">+33 7 51 33 25 07</p>
          </div>
          <div>
            <p className="text-[11px] tracking-widest uppercase text-[#090708]/40 mb-2">{T.contact.hours_title}</p>
            <p className="text-[#090708]/70">{T.footer.hours_line1}</p>
            <p className="text-[#090708]/50 text-sm">{T.footer.hours_line2}</p>
          </div>

          <div className="bg-[#9B6179] text-[#F7F0E8] p-8">
            <p className="font-serif text-xl text-[#F7F0E8] mb-1">{T.contact.newsletter_title}</p>
            <p className="text-sm text-[#F7F0E8]/50 mb-5">{T.contact.newsletter_desc}</p>
            <form onSubmit={subscribeNewsletter} className="flex flex-col gap-3" data-testid="newsletter-form">
              <input
                required type="email" placeholder={T.contact.newsletter_placeholder} value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                className="bg-transparent border-0 border-b border-[#F7F0E8]/25 focus:border-[#E8C47A] focus:ring-0 rounded-none px-0 py-2 text-[#F7F0E8] placeholder:text-[#F7F0E8]/30"
                data-testid="newsletter-input-email"
              />
              <button disabled={sending} type="submit" className="self-start text-[11px] tracking-[0.2em] uppercase text-[#E8C47A] border-b border-[#E8C47A]/40 pb-1" data-testid="newsletter-submit-button">
                {T.contact.newsletter_cta}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
